import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { CONTENT_LOAD_TIMEOUT } from 'reactApp/ui/ReactViewer/ReactViewer.constants';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';

import { useConvertDocumentToPdf } from './hooks/useConvertDocumentToPdf';
import styles from './ViewerPdf.css';

export interface ViewerPdfFallback {
    radarError?: string;
}

export interface ViewerPdfProps {
    url: string;
    renderError?: (fallbackData: ViewerPdfFallback) => ReactElement | null;
    fillParent?: boolean;
    converterStorage?: EStorageType;
    itemStorage?: EStorageType;
    convert?: boolean;
    ext?: string;
    radarError?: string;
}

export const ViewerPdf = memo<ViewerPdfProps>(
    ({ url, renderError = () => null, itemStorage, converterStorage, convert, fillParent = false, ext }) => {
        const ref = useRef<number | undefined>();

        const { fileURL, error } = useConvertDocumentToPdf({ url, convert, converterStorage, ext, itemStorage });

        const [isError, setError] = useState(false);
        const [fallbackData, setFallbackData] = useState<ViewerPdfFallback>({});
        const [isLoaded, setLoaded] = useState(false);

        const src = convert ? fileURL : url;
        const showSpinner = !isLoaded || (convert && !src);

        const handleError = useCallback(() => {
            setFallbackData({ radarError: 'client' });
            setError(true);
            setLoaded(true);
        }, [setError, setLoaded]);

        useEffect(() => {
            if (error) {
                setError(!!error);
                setFallbackData({ radarError: `api_${error.radarName || error.status}` });
            }
        }, [error]);

        const handleLoad = useCallback(() => {
            if (ref.current) {
                clearTimeout(ref.current);
            }
            setLoaded(true);
        }, [ref.current, setLoaded]);

        useEffect(() => {
            if (ref.current) {
                clearTimeout(ref.current);
            }
            ref.current = window.setTimeout(handleError, CONTENT_LOAD_TIMEOUT);

            return () => {
                if (ref.current) {
                    clearTimeout(ref.current);
                }
            };
        }, [url, handleError]);

        if (isError) {
            return renderError(fallbackData);
        }

        return (
            <div
                className={classNames(styles.root, {
                    [styles.root_fillParent]: fillParent,
                })}
            >
                <iframe
                    src={src}
                    className={styles.pdf}
                    allowFullScreen
                    allow="autoplay; fullscreen"
                    frameBorder={0}
                    onError={handleError}
                    onLoad={handleLoad}
                />
                {showSpinner && <Spinner />}
            </div>
        );
    }
);

ViewerPdf.displayName = 'ViewerPdf';

ViewerPdf.whyDidYouRender = true;
